import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonMethodTypes = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Method Types: Instance, Class, & Static</h1>
                <p className="subtitle">Mastering the three flavors of Python methods: From Pizza Factories to Math Utilities.</p>
            </div>

            <div className="content-section">
                <h2>1. The Big Idea (ELI5)</h2>
                <ProTip><p>
                        Imagine a <strong>Pizza Restaurant</strong>. There are three types of jobs:
                    </p>
                    <ul>
                        <li><strong>Instance Method (The Chef):</strong> The Chef (`self`) makes a <em>specific</em> pizza. He touches the dough, adds the cheese. He needs to know about <em>this specific pizza</em> to do his job.</li>
                        <li><strong>Class Method (The Manager):</strong> The Manager (`cls`) doesn't make pizzas. She <em>hires</em> new Chefs. She creates the team. She cares about the "Pizza Place" as a whole, not just one pepperoni slice.</li>
                        <li><strong>Static Method (The Janitor):</strong> The Janitor cleans the floor. He doesn't touch the pizzas (`self`) and he doesn't hire people (`cls`). He is just there to help, doing his own thing independently.</li>
                    </ul>
                    <p>
                        This distinction is critical because choosing the wrong one can make your code harder to test and harder to extend.
                    </p></ProTip>

            </div>

            <div className="content-section">
                <h2>2. The Three Flavors of Methods</h2>
                <p>
                    In Python, not all methods are created equal. When you write a function inside a class, you have three choices:
                </p>
                <table className="premium-table">
                    <thead>
                        <tr>
                            <th>Type</th>
                            <th>Decorator</th>
                            <th>First Argument</th>
                            <th>Role</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><strong>Instance</strong></td>
                            <td>(None)</td>
                            <td>`self`</td>
                            <td>The Chef (Works on one object)</td>
                        </tr>
                        <tr>
                            <td><strong>Class</strong></td>
                            <td>`@classmethod`</td>
                            <td>`cls`</td>
                            <td>The Manager (Works on the Factory)</td>
                        </tr>
                        <tr>
                            <td><strong>Static</strong></td>
                            <td>`@staticmethod`</td>
                            <td>(None)</td>
                            <td>The Janitor (Works alone)</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="content-section">
                <h2>Why separation of duties matters</h2>
                <p>
                    You might be tempted to just use Instance methods for everything.
                    While possible, it leads to messy code.
                    Separating concerns (Creation vs Action vs Utility) makes your code self-documenting.
                    When a developer sees `@classmethod`, they immediately know "Ah, this creates an object".
                    When they see `@staticmethod`, they know "Safe to ignore, this doesn't touch my object state".
                </p>
            </div>


            <div className="content-section">
                <h2>3. Instance Methods (The Chef)</h2>
                <p>
                    These are the standard. They take `self` because they need to know WHICH object they are modifying.
                </p>

                <CodeBlock language="python" code={`class PizzaChef:
    def __init__(self, name):
        self.name = name
        self.pizzas_made = 0

    # Instance Method: Needs 'self' to update 'pizzas_made'
    def make_pizza(self):
        self.pizzas_made += 1
        print(f"Chef {self.name} made a pizza! Total: {self.pizzas_made}")

mario = PizzaChef("Mario")
luigi = PizzaChef("Luigi")

mario.make_pizza() # Chef Mario made a pizza! Total: 1
luigi.make_pizza() # Chef Luigi made a pizza! Total: 1`} />
            </div>

            <div className="content-section">
                <h2>4. Class Methods (The Manager)</h2>
                <p>
                    Class methods don't care about individual chefs. They care about the <strong>Class</strong> itself.
                    We mostly use them as <strong>Alternative Constructors</strong> (Factories) to create new objects.
                </p>

                <CodeBlock language="python" code={`class Pizza:
    def __init__(self, ingredients):
        self.ingredients = ingredients

    def __repr__(self):
        return f"Pizza({self.ingredients})"

    # Factory 1: The Margherita Special
    @classmethod
    def margherita(cls):
        # 'cls' is the Pizza class!
        return cls(['mozzarella', 'tomatoes', 'basil'])

    # Factory 2: The Pepperoni Special
    @classmethod
    def pepperoni(cls):
        return cls(['mozzarella', 'tomatoes', 'pepperoni'])
    
    # Factory 3: From CSV String
    @classmethod
    def from_string(cls, str_data):
        # "cheese,ham" -&gt; ['cheese', 'ham']
        ingredients = str_data.split(',')
        return cls(ingredients)

# Usage
# We don't need to manually list ingredients. The Factory does it.
lunch = Pizza.margherita()
dinner = Pizza.from_string("cheese,ham")

print(lunch)  # Pizza(['mozzarella', 'tomatoes', 'basil'])`} />

                <ProTip><p><strong>Why `cls`?</strong> If we make a new class `class DeepDish(Pizza)`, `cls` will automatically become `DeepDish`. So `DeepDish.margherita()` creates a Deep Dish pizza! Magic!</p></ProTip>
                <p>
                    This is dynamic. If we had just hardcoded `return Pizza(...)`, subclassing would break because `DeepDish.margherita()` would still return a standard `Pizza`.
                    Always use `cls` in factories to support inheritance.
                </p>
            </div>

            <div className="content-section">
                <h2>5. Static Methods (The Janitor)</h2>
                <p>
                    Static methods are just regular functions that live inside a class because it "looks nice" (organization).
                    They <strong>cannot</strong> touch `self` or `cls`.
                </p>

                <CodeBlock language="python" code={`class MathUtils:
    
    @staticmethod
    def add(a, b):
        return a + b

    @staticmethod
    def is_positive(number):
        return number &gt; 0

# Usage
# No object needed! Just call the class.
print(MathUtils.add(5, 10)) # 15`} />
                <ProTip><p><strong>Design Choice:</strong> You might ask, "Why not just write a global function?" You can! But putting it inside the class keeps your code organized (Namespacing). `MathUtils.add` is cleaner than a random `add_numbers` function floating in your file.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>6. Deep Dive: Bound vs Unbound Methods</h2>
                <p>
                    What actually happens when you access `mario.make_pizza`?
                    It's not just a simple function call. Python performs a dynamic binding process at runtime.
                </p>
                <CodeBlock language="python" code={`class A:
    def foo(self):
        print("Foo")

a = A()
print(a.foo) 
# Output: <bound method A.foo of <__main__.A object at ...>>`} />
                <p>
                    Python creates a tiny wrapper called a <strong>Bound Method</strong>.
                    It glues the object `a` to the function `foo` to create a partial function.
                    So when you call `a.foo()`, Python secretly runs `A.foo(a)`.

                    **Why does this matter?**
                    Every time you access `a.foo`, Python creates a NEW method object in memory.
                    If you do this in a tight loop millions of times, it can be slow.
                    This is why for high-performance code, we sometimes assign the method to a variable first (`local_foo = a.foo`) outside the loop.
                </p>
                <ProTip><p><strong>Pro Tip:</strong> You can inspect this! `print(a.foo.__self__)` will show you the object `a` that is stuck to the method!</p></ProTip>
                <p>
                    If you access it via the Class `A.foo`, it is an <strong>Unbound Method</strong> (or just a Function in Python 3).
                </p>
            </div>

            <div className="content-section">
                <h2>7. Summary: The Decision Matrix</h2>
                <ProTip><ul>
                        <li>Does it need to access `self.variable`? -&gt; <strong>Instance Method</strong>.</li>
                        <li>Does it need to create a new object instance? -&gt; <strong>Class Method</strong>.</li>
                        <li>Does it generally relate to the class but touches no variables? -&gt; <strong>Static Method</strong>.</li>
                    </ul></ProTip>
            </div>



            <div className="content-section">
                <h2>8. Extended Technical Reference</h2>

                <h3>Java `static` vs Python `@staticmethod`</h3>
                <p>
                    In Java, `static` is used for global constants and utility methods.
                    Python is similar, but there is a nuance:
                </p>
                <ul className="premium-list">
                    <li><strong>Java:</strong> Static methods are purely class-level. They cannot be overridden in a polymorphic way easily.</li>
                    <li><strong>Python:</strong> Static methods are just functions in a namespace. But Class Methods (`@classmethod`) ARE polymorphic because they receive `cls`. This is why Python prefers Class Methods for Factories.</li>
                </ul>

                <h3>Deep Dive: How Decorators Work</h3>
                <p>
                    `@staticmethod` and `@classmethod` are actually <strong>Descriptors</strong>.
                    When you access `obj.method`, the descriptor magic determines whether to pass `self`, `cls`, or nothing.
                </p>
                <CodeBlock language="python" code={`# What Python actually does:
# MyClass.my_method(obj) 
# is effectively:
# MyClass.__dict__['my_method'].__get__(obj, MyClass)`} />

                <h3>Which one is faster?</h3>
                <p>
                    Technically, `@staticmethod` is slightly faster than an Instance Method because Python doesn't need to create a partially bound function (the "Bound Method" object).
                    However, the difference is measured in nanoseconds. <strong>Always optimize for readability first.</strong>
                </p>
            </div>

        </div >
    );
};

export default PythonMethodTypes;


